import * as React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useLayoutEffect,
  useState,
  useMemo,
} from 'react';
import type {Key, Selection} from '@react-types/shared';
import {
  AnimatePresenceProvider,
  assignProps,
  createView,
  createViewConfig,
  view,
} from '@sail/engine';
import {arrowUpDown} from '@sail/icons/react/Icon';
import type {View} from '@sail/engine';
import type {
  Expandable,
  Filterable,
  ListCollectionComponent,
  MenuTriggerProps,
  MultiSelectState,
  SingleSelectionProps,
  MultipleSelectionProps,
  TreeState,
  ListCollection,
  CollectionItem,
} from '@sail/react-aria';
import {
  FocusScope,
  mapSelectionToKeysSet,
  useListCollection,
  useMultiSelectState,
  useSelect,
} from '@sail/react-aria';
import {message, useIntl} from '@sail/intl';
import {FormField, useFormFieldProps} from './FormField';
import {Icon} from './Icon';
import {Button} from './Button';
import {Popover} from './popover/Popover';
import {ListBox, ListBoxContext, ListBoxConfig} from './ListBox';
import {useFieldContexts} from '../hooks/useFieldContexts';
import type {FormFieldProps} from './FormField';
import type {ButtonProps} from './Button';
import {SearchField, SearchFieldContext} from './SearchField';
import {
  DO_NOT_USE_FauxTree as FauxTree,
  FauxTreeContext,
  FauxTreeConfig,
} from './FauxTree';
import type {FauxTreeGroupProps} from './FauxTreeGroup';
import {
  FauxTreeGroupTransforms,
  FauxTreeGroup,
  FauxTreeGroupConfig,
} from './FauxTreeGroup';
import type {FauxTreeItemProps} from './FauxTreeItem';
import {FauxTreeItemTransforms, FauxTreeItem} from './FauxTreeItem';
import {ListBoxGroup, ListBoxGroupConfig} from './ListBoxGroup';
import {ListBoxItem} from './ListBoxItem';
import {SelectFieldConfig} from './SelectFieldConfig';
import {Tooltip} from './Tooltip';
import {
  normalizeSelectionProps,
  validateSelectionProps,
} from './utils/selectionHelpers';
import {getVisibleItemsAndOverflow} from '../lib/getVisibleItemsAndOverflow';
import {useResizeObserver} from './hooks/useResizeObserver';
import {useMediaQuery} from '../providers/UiMediaQueryProvider';
import {useSelectFieldFormAdapter} from './form/adapters/useSelectFieldFormAdapter';
import {useFormContext} from './form/useFormContext';
import {css} from '../css';
import {LABEL_OPT_OUT_STRING} from './hooks/useInputId';
import {useShouldDisplayBottomSheet} from './bottom-sheet/useShouldDisplayBottomSheet';
import {getCollectionGroupKey} from './CollectionGroupContext';
/* eslint-disable @stripe-internal/stripe/hooks-in-createview */

type MessagesConfig = {
  selectedText?: (t: number) => string;
  numberOfItemsText?: (t: number) => string;
  andMoreText?: (t: number) => string;
};

export type SelectedValueDisplay = {
  /**
   * Include plain-text group labels in the closed trigger and overflow tooltip.
   * Applies only to grouped items and defaults to false. Fully selected groups
   * are compacted to their group label in multi-select fields.
   */
  showGroupLabels?: boolean;
};

const andMoreItemsMessage = message<{count: number}>({
  id: '@sail/ui.selectField.andMore',
  description:
    'Text shown when there are more selected items than can be displayed',
  defaultMessage: 'and {count} more',
});

const numberOfItemsMessage = message<{count: number}>({
  id: '@sail/ui.selectField.numberOfItems',
  description:
    'Text shown when selected items overflow and only the count is displayed',
  defaultMessage: '{count} items',
});

const allSelectedMessage = message<{count: number}>({
  id: '@sail/ui.selectField.allSelected',
  description: 'Text shown in the trigger when every item is selected',
  defaultMessage: 'All {count} selected',
});

const useAutofocus = (
  state: MultiSelectState & TreeState,
  isMultiSelect: boolean,
) => {
  const autoFocusRef = useRef(true);

  useEffect(() => {
    // Reset the autofocus flag when the list closes or opens.
    autoFocusRef.current = true;
  }, [state.isOpen]);

  useEffect(() => {
    // If the list is open and there are items, focus the first selected item.
    // If there's no selected item, leave focus on the list itself instead of
    // focusing the first item, to maintain compatibility with the previous behavior.
    if (autoFocusRef.current && state.isOpen && state.collection.size > 0) {
      const manager = state.selectionManager;
      const firstSelectedItem = Array.from(manager.selectedKeys).find((key) => {
        if (!manager.canSelectItem(key)) {
          return false;
        }
        // Autofocusing an item that lives inside a collapsed group forces
        // that group open via FauxTreeGroup's expand-ancestors-of-focused-item
        // effect, overriding whatever `defaultExpandedKeys`/`expandedKeys` the
        // consumer configured. Multi-select lists default to collapsed
        // groups, so skip focusing into one to keep it collapsed on open.
        if (isMultiSelect && state.expandable) {
          let parentKey = state.collection.getItem(key)?.parentKey;
          while (parentKey) {
            if (!state.expandedKeys.has(parentKey as string)) {
              return false;
            }
            const parent = state.collection.getItem(parentKey);
            parentKey =
              parentKey === parent?.parentKey ? undefined : parent?.parentKey;
          }
        }
        return true;
      });

      if (firstSelectedItem) {
        manager.setFocused(true);
        manager.setFocusedKey(firstSelectedItem);
      }

      autoFocusRef.current = false;
    }
  }, [state, isMultiSelect]);
};

const getCollectionItemsLength = (
  collection: ListCollection<FauxTreeItemProps, FauxTreeGroupProps>,
) => {
  return collection.getItems().reduce((acc, item) => {
    if (item.childItems) {
      return acc + item.childItems.length;
    } else {
      return acc + 1;
    }
  }, 0);
};

type SelectedItem = NonNullable<
  NonNullable<MultiSelectState['selectedItems']>[number]
>;

type SelectionPreview = {
  key: Key;
  textValue: string;
  content: React.ReactNode;
  separator: string;
  itemCount: number;
  grouped: boolean;
};

const getSelectedItemContent = (item: SelectedItem) =>
  item.props?.children || item.textValue;

const getItemGroups = (items: CollectionItem[]) => {
  const sections = new Map<Key, CollectionItem>(
    items
      .filter((item) => item.type === 'section')
      .map((section) => [section.key, section]),
  );
  const groups = new Map<Key, CollectionItem>();
  const explicitSections = new Set<CollectionItem>();
  let pendingItems: CollectionItem[] = [];

  items.forEach((item) => {
    const groupKey = getCollectionGroupKey(item.data);
    const section = groupKey === undefined ? undefined : sections.get(groupKey);
    if (item.type !== 'section' && section) {
      groups.set(item.key, section);
      explicitSections.add(section);
    }
  });

  items.forEach((item) => {
    if (item.type !== 'section') {
      if (!groups.has(item.key)) {
        pendingItems.push(item);
      }
      return;
    }

    let groupItems = item.childItems?.length ? item.childItems : [];
    if (groupItems.length === 0 && !explicitSections.has(item)) {
      groupItems = pendingItems.filter(
        (pendingItem) => pendingItem.indexPathString === item.indexPathString,
      );
    }
    const groupKeys = new Set(groupItems.map((groupItem) => groupItem.key));
    groupItems.forEach((groupItem) => groups.set(groupItem.key, item));
    pendingItems = pendingItems.filter(
      (pendingItem) => !groupKeys.has(pendingItem.key),
    );
  });

  return groups;
};

const getSelectionPreviews = (
  selectedItems: SelectedItem[],
  collection: ListCollection<FauxTreeItemProps, FauxTreeGroupProps>,
  compactFullGroups: boolean,
): SelectionPreview[] => {
  const collectionItems = collection.getItems();
  const groups = getItemGroups(collectionItems);
  const selectedKeys = new Set(selectedItems.map((item) => item.key));
  const selectedItemsByKey = new Map(
    selectedItems.map((item) => [item.key, item]),
  );
  const visitedGroups = new Set<CollectionItem>();
  const previews: SelectionPreview[] = [];

  const addGroup = (section: CollectionItem) => {
    const sectionTextValue = section.textValue;
    if (!sectionTextValue || visitedGroups.has(section)) {
      return;
    }
    visitedGroups.add(section);

    const memberKeys = [...groups.entries()]
      .filter(([, group]) => group === section)
      .map(([key]) => key);
    const chosen = selectedItems.filter(
      (item) => groups.get(item.key) === section,
    );

    if (
      compactFullGroups &&
      memberKeys.length > 0 &&
      memberKeys.every((key) => selectedKeys.has(key))
    ) {
      previews.push({
        key: section.key,
        textValue: sectionTextValue,
        content: sectionTextValue,
        separator: '. ',
        itemCount: chosen.length,
        grouped: true,
      });
      return;
    }

    chosen.forEach((item, index) => {
      const prefix = index === 0 ? `${sectionTextValue}: ` : '';
      const itemContent = getSelectedItemContent(item);
      previews.push({
        key: item.key,
        textValue: `${prefix}${item.textValue}`,
        content:
          typeof itemContent === 'string' ? (
            <>
              {prefix ? <view.span>{prefix}</view.span> : null}
              <view.span>{itemContent}</view.span>
            </>
          ) : (
            <>
              {prefix ? <view.span>{prefix}</view.span> : null}
              {itemContent}
            </>
          ),
        separator: index === chosen.length - 1 ? '. ' : ', ',
        itemCount: 1,
        grouped: true,
      });
    });
  };

  const addUngroupedItem = (key: Key) => {
    const item = selectedItemsByKey.get(key);
    if (item) {
      previews.push({
        key: item.key,
        textValue: item.textValue,
        content: getSelectedItemContent(item),
        separator: ', ',
        itemCount: 1,
        grouped: false,
      });
    }
  };

  collectionItems.forEach((item) => {
    if (item.type === 'section') {
      if (item.textValue) {
        addGroup(item);
      } else {
        item.childItems?.forEach((child) => addUngroupedItem(child.key));
      }
      return;
    }

    const section = groups.get(item.key);
    if (section?.textValue) {
      addGroup(section);
    } else {
      addUngroupedItem(item.key);
    }
  });

  const lastPreview = previews[previews.length - 1];
  if (lastPreview) {
    lastPreview.separator = '';
  }
  return previews;
};

const useVisibleSelectedItems = <
  T extends {textValue: string; itemCount?: number},
>(
  selectedItems: (T | null)[] | null | undefined,
  itemsTotalCount: number,
  itemsContainerRef: React.RefObject<HTMLDivElement>,
  onItemsVisibleChange: (visible: boolean) => void,
) => {
  const [visibleItemsCount, setVisibleItemsCount] = useState<number>(0);
  const [firstItemOverflows, setFirstItemOverflows] = useState<boolean>(false);
  const [firstItemOverflowText, setFirstItemOverflowText] =
    useState<string>('');

  const itemsRef = useRef<React.MutableRefObject<HTMLSpanElement>[]>(
    Array.from(
      {length: itemsTotalCount},
      () =>
        React.createRef<HTMLSpanElement>() as React.MutableRefObject<HTMLSpanElement>,
    ),
  );

  useLayoutEffect(() => {
    itemsRef.current = Array.from(
      {length: itemsTotalCount},
      () =>
        React.createRef<HTMLSpanElement>() as React.MutableRefObject<HTMLSpanElement>,
    );
  }, [itemsTotalCount]);

  const measure = useCallback(() => {
    if (!selectedItems || !itemsContainerRef.current || !itemsRef.current) {
      return;
    }

    const {visibleItemsCount, firstItemOverflows, firstItemOverflowText} =
      getVisibleItemsAndOverflow(selectedItems, itemsContainerRef, itemsRef);

    setVisibleItemsCount(visibleItemsCount);
    setFirstItemOverflows(firstItemOverflows);
    setFirstItemOverflowText(firstItemOverflowText);

    onItemsVisibleChange(
      !firstItemOverflows && visibleItemsCount === selectedItems.length,
    );
  }, [selectedItems, itemsContainerRef, onItemsVisibleChange]);

  useLayoutEffect(measure, [measure]);

  // The initial measurement can land before the trigger has real layout
  // (e.g. while a Drawer is still sliding open), which the fallback below
  // treats conservatively rather than as "everything fits". Re-measure
  // whenever the container's size actually changes so that case — and any
  // later resize (sidebar collapse, window resize, etc.) — corrects itself
  // instead of getting stuck on that first, possibly-zero-width reading.
  useResizeObserver({
    node: itemsContainerRef,
    handler: measure,
    avoidInitialEvent: true,
  });

  return {visibleItemsCount, firstItemOverflows, firstItemOverflowText};
};

const getSelectedItemTooltipText = (item: SelectedItem | null) => {
  if (!item) {
    return '';
  }

  if (typeof item.props.children === 'string') {
    return item.props.children;
  }

  return item.textValue;
};

const MultipleSelectionButtonContent: React.FC<{
  selectedItems: MultiSelectState['selectedItems'];
  itemsTotalCount: number;
  messages?: MessagesConfig;
  onItemsVisibleChange: (visible: boolean) => void;
}> = ({itemsTotalCount, selectedItems, messages, onItemsVisibleChange}) => {
  const {formatMessage} = useIntl();
  const itemsContainerRef = useRef<HTMLDivElement>(null);

  const allSelected =
    itemsTotalCount > 0 && selectedItems?.length === itemsTotalCount;

  const {visibleItemsCount, firstItemOverflows, firstItemOverflowText} =
    useVisibleSelectedItems(
      selectedItems,
      itemsTotalCount,
      itemsContainerRef,
      onItemsVisibleChange,
    );

  const hiddenItemsCount = (selectedItems?.length ?? 0) - visibleItemsCount;
  // The full item list is more useful than the generic summary, so only
  // show the summary once the list wouldn't otherwise fit — either because
  // some items are hidden behind an "and N more" suffix, or because even the
  // first item's own label doesn't fit. A single-item selection never shows
  // the summary, since "All 1 selected" is redundant with just showing that
  // one item.
  const showAllSelectedText =
    allSelected &&
    itemsTotalCount > 1 &&
    (hiddenItemsCount > 0 || firstItemOverflows);

  useEffect(() => {
    if (showAllSelectedText) {
      onItemsVisibleChange(true);
    }
  }, [showAllSelectedText, onItemsVisibleChange]);

  if (!selectedItems || selectedItems.length === 0) {
    return null;
  }

  if (showAllSelectedText) {
    return (
      // The item list below still needs to mount with this same ref so its
      // measurement effect can run and re-evaluate `showAllSelectedText` if
      // the selection changes — an early return without it would never
      // re-measure once every item is selected.
      <view.div ref={itemsContainerRef}>
        <view.span
          css={{
            overflowX: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {messages?.numberOfItemsText
            ? messages.numberOfItemsText(itemsTotalCount)
            : formatMessage(allSelectedMessage, {count: itemsTotalCount})}
        </view.span>
      </view.div>
    );
  }

  const andMoreSuffix =
    hiddenItemsCount > 0
      ? ` ${
          messages?.andMoreText
            ? messages.andMoreText(hiddenItemsCount)
            : formatMessage(andMoreItemsMessage, {count: hiddenItemsCount})
        }`
      : '';

  return (
    <view.div
      ref={itemsContainerRef}
      css={{
        flexGrow: 1,
        stack: 'x',
        gap: 'xsmall',
        overflowX: 'hidden',
      }}
    >
      <view.div
        css={
          firstItemOverflows
            ? css({
                overflowX: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              })
            : css({flexGrow: 0})
        }
      >
        {firstItemOverflows && firstItemOverflowText ? (
          <view.span>
            {firstItemOverflowText}
            {andMoreSuffix}
          </view.span>
        ) : (
          Array.from({length: visibleItemsCount}).map((_, index) => {
            const selectedItem = selectedItems[index];

            if (!selectedItem) {
              return null;
            }

            const isLastVisible = index + 1 === visibleItemsCount;

            return (
              <React.Fragment key={selectedItem.key}>
                <view.span>
                  {selectedItem.props?.children || selectedItem.textValue}
                  {!isLastVisible ? ', ' : andMoreSuffix}
                </view.span>
              </React.Fragment>
            );
          })
        )}
      </view.div>
      {visibleItemsCount === 0 ? (
        <view.span
          css={{
            overflowX: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {messages?.numberOfItemsText
            ? messages.numberOfItemsText(selectedItems.length)
            : formatMessage(numberOfItemsMessage, {
                count: selectedItems.length,
              })}
        </view.span>
      ) : null}
    </view.div>
  );
};

const GroupedMultipleSelectionButtonContent: React.FC<{
  selectedItems: SelectionPreview[];
  selectedItemsCount: number;
  itemsTotalCount: number;
  messages?: MessagesConfig;
  onItemsVisibleChange: (visible: boolean) => void;
}> = ({
  itemsTotalCount,
  selectedItems,
  selectedItemsCount,
  messages,
  onItemsVisibleChange,
}) => {
  const {formatMessage} = useIntl();
  const itemsContainerRef = useRef<HTMLDivElement>(null);

  const {visibleItemsCount, firstItemOverflows, firstItemOverflowText} =
    useVisibleSelectedItems(
      selectedItems,
      itemsTotalCount,
      itemsContainerRef,
      onItemsVisibleChange,
    );

  if (selectedItems.length === 0) {
    return null;
  }

  const hiddenItemsCount = selectedItems
    .slice(visibleItemsCount)
    .reduce((count, item) => count + item.itemCount, 0);
  const andMoreSuffix =
    hiddenItemsCount > 0
      ? ` ${
          messages?.andMoreText
            ? messages.andMoreText(hiddenItemsCount)
            : formatMessage(andMoreItemsMessage, {count: hiddenItemsCount})
        }`
      : '';

  return (
    <view.div
      ref={itemsContainerRef}
      css={{
        flexGrow: 1,
        stack: 'x',
        gap: 'xsmall',
        overflowX: 'hidden',
      }}
    >
      <view.div
        css={
          firstItemOverflows
            ? css({
                overflowX: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              })
            : css({flexGrow: 0})
        }
      >
        {firstItemOverflows && firstItemOverflowText ? (
          <view.span>
            {firstItemOverflowText}
            {andMoreSuffix}
          </view.span>
        ) : (
          Array.from({length: visibleItemsCount}).map((_, index) => {
            const selectedItem = selectedItems[index];

            if (!selectedItem) {
              return null;
            }

            const isLastVisible = index + 1 === visibleItemsCount;

            return (
              <React.Fragment key={selectedItem.key}>
                <view.span>
                  {selectedItem.content}
                  {!isLastVisible ? selectedItem.separator : andMoreSuffix}
                </view.span>
              </React.Fragment>
            );
          })
        )}
      </view.div>
      {visibleItemsCount === 0 ? (
        <view.span
          css={{
            overflowX: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {messages?.numberOfItemsText
            ? messages.numberOfItemsText(selectedItemsCount)
            : formatMessage(numberOfItemsMessage, {
                count: selectedItemsCount,
              })}
        </view.span>
      ) : null}
    </view.div>
  );
};

const TruncatedText = createView('span', {
  css: {
    overflowX: 'hidden',
    overflowY: 'visible',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
});

interface ListContextValue {
  selectionMode?: SelectionProps['selectionMode'];
}

const ListContext = createContext<ListContextValue>({});

export const useListContext = () => {
  return useContext(ListContext);
};

// Item

export type SelectFieldItemProps = FauxTreeItemProps;
export const SelectFieldItemTransforms = FauxTreeItemTransforms;

export const SelectFieldItemConfig = createViewConfig({
  name: 'SelectItem',
  props: {} as SelectFieldItemProps,
  flattens: true,
});

export const SelectFieldItem = SelectFieldItemConfig.createView(
  (props: FauxTreeItemProps) => {
    const {selectionMode} = useListContext();
    return selectionMode === 'multiple' ? (
      <FauxTreeItem {...props} />
    ) : (
      <ListBoxItem {...props} />
    );
  },
);

// Group

export type SelectFieldGroupProps = FauxTreeGroupProps;
export const SelectFieldGroupTransforms = FauxTreeGroupTransforms;

export const SelectFieldGroupConfig = createViewConfig({
  name: 'SelectGroup',
  props: {} as SelectFieldGroupProps,
  flattens: true,
});

export const SelectFieldGroup = SelectFieldGroupConfig.createView(
  (props: FauxTreeGroupProps) => {
    const {selectionMode} = useListContext();
    return selectionMode === 'multiple' ? (
      <FauxTreeGroup {...props} />
    ) : (
      <ListBoxGroup {...props} />
    );
  },
);

// SelectField

export type SelectionProps<CanClearSelection extends boolean = false> = Omit<
  SingleSelectionProps & MultipleSelectionProps,
  'disallowEmptySelection' | 'onSelectionChange'
> & {
  /** Handler that is called when the selection changes for multiple-selection fields. */
  onMultiSelectionChange?: (keys: Selection) => void;

  /** Handler that is called when the selection changes for single-selection fields. */
  onSelectionChange?: (
    key: [CanClearSelection] extends [true] ? Key | null : Key,
  ) => unknown;
};

export type SelectFieldProps<
  T,
  CanClearSelection extends boolean = false,
> = FormFieldProps<
  ListCollectionComponent<
    T,
    SelectFieldItemProps,
    typeof SelectFieldItemTransforms.defaultField,
    SelectFieldGroupProps,
    typeof SelectFieldGroupTransforms.idField
  > &
    Expandable &
    Filterable<T> &
    SelectionProps<CanClearSelection> &
    MenuTriggerProps & {
      /** The placeholder text to display when no value is selected */
      placeholder?: string;
      /** The placeholder text to display in the filter search field when filterable is true */
      filterPlaceholder?: string;
      /** Whether the input is disabled. */
      disabled?: boolean;
      disabledKeys?: Iterable<Key>;
      /** Whether the input can be selected but not changed by the user. */
      readOnly?: boolean;
      subviews?: View.Subviews<{
        button: typeof Button;
        buttonIcon: typeof Icon;
        popover: typeof Popover;
        value: 'span';
      }>;
      size?: ButtonProps['size'];
      ['data-testid']?: string;
      messages?: MessagesConfig;
      /**
       * Controls how selected values are presented in the closed trigger and
       * overflow tooltip. Group labels are hidden by default; when enabled,
       * fully selected groups are compacted in multi-select fields.
       */
      selectedValueDisplay?: SelectedValueDisplay;
      /**  Set to false to hide the option to select/unselect all items */
      selectionAllEnabled?: boolean;
      /**
       * Enable the clear selection option.
       * Only for `selectionMode=single`. Multi-selection fields already support clearing the selection by default.
       *
       * Requires that the `placeholder` prop is defined.
       * */
      clearable?: CanClearSelection;
      /**
       * Set to false to hide the option to select/unselect all items in a group.
       *
       * Only applies when `group` prop is set and `selectionMode=multiple`.
       */
      groupSelectionAllEnabled?: boolean;
    },
  typeof Button
>;

export const SelectField = SelectFieldConfig.createGenericView(
  <T extends object, CanClearSelection extends boolean = false>({
    data,
    item,
    group,
    children,
    placeholder,
    filterPlaceholder,
    filterable,
    defaultFilter,
    messages,
    selectedValueDisplay,
    size,
    selectionAllEnabled,
    clearable,
    groupSelectionAllEnabled,
    ...restProps
  }: View.RenderProps<SelectFieldProps<T, CanClearSelection>>) => {
    validateSelectionProps(restProps);

    const isInSailForm = !!useFormContext()?.control;
    const isMultiSelect = restProps.selectionMode === 'multiple';

    const {valueAdapter, changeAdapter} =
      useSelectFieldFormAdapter(isMultiSelect);

    const [fieldProps, props] = useFormFieldProps(restProps, {
      valueProp: isMultiSelect ? 'selectedKeys' : 'selectedKey',
      changeProp: isMultiSelect
        ? 'onMultiSelectionChange'
        : 'onSelectionChange',
      valueAdapter,
      changeAdapter,
    });

    const {label} = fieldProps;

    const collection = useListCollection({
      children,
      data,
      item,
      group,
      itemTransforms: SelectFieldItemTransforms,
      groupTransforms: SelectFieldGroupTransforms,
    });

    if (isInSailForm && !isMultiSelect) {
      const singleChangeHandler = props.onSelectionChange;
      props.onSelectionChange = (key) => {
        singleChangeHandler?.(key);
        // For single selection, call onBlur because dropdown closes
        if ('onBlur' in props) {
          props.onBlur?.();
        }
      };
    }

    const selectedKeysRef = useRef<Iterable<Key> | undefined>();

    const state = useMultiSelectState({
      getItems: collection.getItems,
      selection: {
        ...props,
        ...normalizeSelectionProps(props, collection, true, selectedKeysRef),
      },
      trigger: props,
      expandable: props,
    });

    selectedKeysRef.current = state.selectedKeys;

    const buttonRef = useRef<HTMLAnchorElement>(null);

    const {labelProps, triggerProps, valueProps, listboxProps} = useSelect(
      {isDisabled: props.disabled || props.readOnly || false},
      state,
      buttonRef,
    );

    const [triggerTooltipEnabled, setTriggerTooltipEnabled] =
      useState<boolean>(false);

    if (children && filterable) {
      throw new Error(
        'SelectField with the `filterable` prop was provided items as JSX children. This is not supported; ' +
          'to use filtering, provide items with the `data` prop',
      );
    }

    // Autofocus the first item when the list opens. This is a workaround to make autofocus
    // work with our collections API. Our collections are built differently from react-aria's
    // collections. We only add items to the collection when they're rendered while react-aria
    // expects all items to be in the collection before the list is opened. This means that when
    // the list is opened, react-aria has already triggered focus on an item that doesn't exist
    // in the collection and we need to manually trigger focus.
    useAutofocus(state, isMultiSelect);

    const {isTouch, isSmallScreen} = useMediaQuery();
    const isMobileAndTouch = isSmallScreen && isTouch;

    const shouldDisplayBottomSheet = useShouldDisplayBottomSheet('popover');

    const searchFieldProps = {
      label,
      ...(filterPlaceholder ? {placeholder: filterPlaceholder} : {}),
      data,
      defaultFilter,
      item,
      group,
      expandable: props.expandable,
      defaultExpandedKeys: props.defaultExpandedKeys,
      expandedKeys: props.expandedKeys,
      disabledKeys: props.disabledKeys,
      defaultValue: '',
      shouldCloseOnBlur: false,
      isOpen: true, // If we are rendering the searchfield, we always want to render the FauxTree below it
      css: [
        css({
          borderRadius: 0,
          backgroundColor: 'form',
          // match Popover's border radius
          borderTopLeftRadius: 'medium',
          borderTopRightRadius: 'medium',
        }),
        isMobileAndTouch
          ? css({borderBottom: 'none'})
          : css({borderBottom: 'solid 1px', borderBottomColor: 'border'}),
      ],
      subviews: {
        inputs: {
          css: [
            css({
              borderTopLeftRadius: 3,
              borderTopRightRadius: 3,
              // margin: xsmall is used to provision enough space for the focus ring
              // in order to visually appear "inset"
              margin: 'xsmall',
            }),
            isMobileAndTouch
              ? css({
                  borderBottomLeftRadius: 'xsmall',
                  borderBottomRightRadius: 'xsmall',
                  keyline: 'border',
                  marginX: 'space.175',
                  marginTop: 'space.175',
                  marginBottom: 'xsmall',
                  borderBottom: 'none',
                })
              : css({
                  keyline: 'transparent',
                  ':focus-within': {
                    keyline: 'transparent',
                  },
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }),
            shouldDisplayBottomSheet
              ? css({
                  marginX: 'space.250',
                  marginTop: 'space.0',
                })
              : css({}),
          ],
        },
      },
    };

    const itemVisibleChangeHandler = useCallback(
      (allItemsVisible: boolean) => setTriggerTooltipEnabled(!allItemsVisible),
      [setTriggerTooltipEnabled],
    );

    const shouldShowClearSelection =
      !!clearable && !!placeholder && !isMultiSelect;

    if (clearable && !shouldShowClearSelection) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          'SelectField: "clearable" is enabled, but the clear selection item will not be shown. You are probably missing a `placeholder` prop, or are using `selectionMode=multiple`.',
        );
      }
    }

    const singleSelectNonFilterableListboxRef = useRef<HTMLDivElement>(null);

    /**
     * Disable pointer events when the popover is closing.
     * Due to `shouldFocusOnHover`, if we hover another SelectFieldItem while the popover is animating out,
     * the new item would be focused, overriding any focus logic that might be happening in onSelectionChange/onMultiSelectionChange.
     *
     * Ideally, this would be applied to all selectionMode+filterable SelectField combinations, but this is the most important one for now.
     */
    useLayoutEffect(() => {
      if (singleSelectNonFilterableListboxRef.current) {
        singleSelectNonFilterableListboxRef.current.style.pointerEvents =
          state.isOpen ? 'auto' : 'none';
      }
    }, [state.isOpen]);

    const listbox = (
      <AnimatePresenceProvider>
        <FocusScope restoreFocus autoFocus={filterable}>
          <ListBoxConfig.Customize
            uses={[
              css({
                keyline: 'transparent',
                backgroundColor: 'form',
              }),
              shouldDisplayBottomSheet
                ? css({
                    paddingX: 'space.125',
                  })
                : css({}),
            ]}
          >
            <ListBoxGroupConfig.Customize
              uses={[
                shouldDisplayBottomSheet
                  ? css({
                      bleedX: 'space.125',
                      paddingX: 'space.125',
                    })
                  : css({}),
              ]}
            >
              {filterable ? (
                <SearchFieldContext.Provider
                  value={{collection, options: {shouldUsePopover: false}}}
                >
                  <SearchField
                    {...searchFieldProps}
                    menuTrigger="focus"
                    hiddenElements={['label']}
                    size={isMobileAndTouch ? 'large' : undefined}
                    _SAIL_INTERNAL_USE_clearable={
                      shouldShowClearSelection
                        ? {label: placeholder}
                        : undefined
                    }
                    selectedKey={
                      [...mapSelectionToKeysSet(state.selectedKeys)]?.[0] ??
                      null
                    }
                    subviews={{
                      ...searchFieldProps.subviews,
                      listbox: {
                        css: [
                          isMobileAndTouch
                            ? css({
                                flexGrow: '0',
                                alignY: 'top',
                                height: 'fill',
                              })
                            : css({}),
                          css({
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                          }),
                        ],
                      },
                    }}
                    onSelectionChange={(key) => {
                      if (key) {
                        state.setSelectedKeys(new Set([key]));
                        state.close();
                      } else if (shouldShowClearSelection) {
                        state.setSelectedKeys(new Set([]));
                        state.close();
                      }
                    }}
                    onSubmit={(_value, results) => {
                      if (!state.isFocused) {
                        const key = Array.from(results).find(
                          (key) =>
                            state.collection.getItem(key)?.type !== 'section',
                        );

                        if (key) {
                          state.setSelectedKeys(new Set([key]));
                          state.close();
                        }
                      }
                    }}
                  />
                </SearchFieldContext.Provider>
              ) : (
                <ListBoxContext.Provider
                  value={{state, collection, options: listboxProps}}
                >
                  <ListBox
                    ref={singleSelectNonFilterableListboxRef}
                    clearable={
                      shouldShowClearSelection
                        ? {label: placeholder}
                        : undefined
                    }
                  />
                </ListBoxContext.Provider>
              )}
            </ListBoxGroupConfig.Customize>
          </ListBoxConfig.Customize>
        </FocusScope>
      </AnimatePresenceProvider>
    );

    const fauxTree = (
      <AnimatePresenceProvider>
        <FocusScope restoreFocus autoFocus={filterable}>
          <FauxTreeConfig.Customize
            uses={[
              css({
                keyline: 'transparent',
                backgroundColor: 'form',
              }),
              shouldDisplayBottomSheet
                ? css({
                    paddingX: 'space.125',
                  })
                : css({}),
            ]}
          >
            <FauxTreeGroupConfig.Customize
              uses={[
                shouldDisplayBottomSheet
                  ? css({
                      bleedX: 'space.125',
                      paddingX: 'space.125',
                    })
                  : css({}),
              ]}
            >
              {filterable ? (
                <SearchFieldContext.Provider
                  value={{collection, options: {shouldUsePopover: false}}}
                >
                  <SearchField
                    {...searchFieldProps}
                    menuTrigger="focus"
                    hiddenElements={['label']}
                    size={isMobileAndTouch ? 'large' : undefined}
                    selectedKeys={state.selectedKeys}
                    selectionMode="multiple"
                    messages={messages}
                    selectionAllEnabled={selectionAllEnabled}
                    groupSelectionAllEnabled={groupSelectionAllEnabled}
                    subviews={{
                      ...searchFieldProps.subviews,
                      listbox: {
                        css: [
                          isMobileAndTouch
                            ? css({
                                overflowY: 'auto',
                                alignY: 'top',
                                height: 'fill',
                              })
                            : css({}),
                          css({
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                          }),
                        ],
                      },
                    }}
                    onMultiSelectionChange={(keys: Selection) => {
                      state.setSelectedKeys(keys);
                    }}
                    onSubmit={(_value, results) => {
                      if (!state.isFocused) {
                        const key = Array.from(results).find(
                          (key) =>
                            state.collection.getItem(key)?.type !== 'section',
                        );

                        if (key) {
                          const mappedSelectedKeys = mapSelectionToKeysSet(
                            state.selectedKeys,
                          );

                          const isSelected = mappedSelectedKeys.has(key);
                          if (!isSelected) {
                            state.setSelectedKeys([...mappedSelectedKeys, key]);
                          }
                        }
                      }
                    }}
                  />
                </SearchFieldContext.Provider>
              ) : (
                <FauxTreeContext.Provider
                  value={{state, collection, options: listboxProps}}
                >
                  <FauxTree
                    label={label}
                    data={data}
                    item={item}
                    group={group}
                    messages={messages}
                    selectionAllEnabled={selectionAllEnabled}
                    groupSelectionAllEnabled={groupSelectionAllEnabled}
                  >
                    {children}
                  </FauxTree>
                </FauxTreeContext.Provider>
              )}
            </FauxTreeGroupConfig.Customize>
          </FauxTreeConfig.Customize>
        </FocusScope>
      </AnimatePresenceProvider>
    );

    const {selectedItems} = state;
    const showGroupLabels = selectedValueDisplay?.showGroupLabels ?? false;
    const groupedSelectionPreviews = useMemo(
      () =>
        showGroupLabels
          ? getSelectionPreviews(
              (selectedItems ?? []).filter(
                (selectedItem): selectedItem is SelectedItem =>
                  selectedItem !== null,
              ),
              collection,
              isMultiSelect,
            )
          : [],
      [selectedItems, collection, isMultiSelect, showGroupLabels],
    );

    const itemsLength = useMemo(() => {
      return getCollectionItemsLength(collection);
    }, [collection]);

    let singleSelectionContent: React.ReactNode = null;
    if (props.selectionMode === 'single') {
      singleSelectionContent =
        showGroupLabels && groupedSelectionPreviews[0]?.grouped ? (
          <TruncatedText>{groupedSelectionPreviews[0].content}</TruncatedText>
        ) : (
          selectedItems?.[0]?.props.children || selectedItems?.[0]?.textValue
        );
    }

    let multipleSelectionContent: React.ReactNode = null;
    if (props.selectionMode === 'multiple') {
      multipleSelectionContent = showGroupLabels ? (
        <GroupedMultipleSelectionButtonContent
          itemsTotalCount={itemsLength}
          selectedItems={groupedSelectionPreviews}
          selectedItemsCount={selectedItems?.length ?? 0}
          onItemsVisibleChange={itemVisibleChangeHandler}
          messages={messages}
        />
      ) : (
        <MultipleSelectionButtonContent
          itemsTotalCount={itemsLength}
          selectedItems={selectedItems}
          onItemsVisibleChange={itemVisibleChangeHandler}
          messages={messages}
        />
      );
    }

    return (
      <FormField
        {...fieldProps}
        size={size}
        subviews={{
          label: {
            uses: [assignProps(labelProps as View.IntrinsicElement<'label'>)],
          },
          inputs: {css: {minWidth: '0'}},
        }}
        css={{minWidth: 240}}
      >
        <Button
          ref={buttonRef}
          {...valueProps}
          data-testid={props['data-testid']}
          css={{
            stack: 'x',
            display: 'block',
            baselineAlign: 'auto',
            gap: 'space.75',
            // buttons have `width: 'fit'` by default, but fields stretch to fill the available space
            width: 'auto',
            alignY: 'center',
            // Appearance
            topShadow: 'form',
            keyline: 'form',
            backgroundColor: 'form',
            textColor: 'form',
            border: 'none',
            borderRadius: 'form',

            // Hovered
            ':hover:not(:active):not([aria-disabled="true"])': {
              topShadow: 'form.hovered',
              keyline: 'form.hovered',
              backgroundColor: 'form.hovered',
              textColor: 'form.hovered',
            },
            // Focus
            ':focus': {
              focusRing: 'focus',
              topShadow: 'form.focused',
              keyline: 'form.focused',
              backgroundColor: 'form.focused',
              textColor: 'form.focused',
            },

            ':active:not([aria-disabled="true"])': {
              topShadow: 'form.pressed',
              keyline: 'form.pressed',
              backgroundColor: 'form.pressed',
              textColor: 'form.pressed',
            },

            // Error styles
            '[aria-invalid="true"]': {
              keyline: 'form.error',
            },

            // Disabled styles
            '[aria-disabled="true"]': {
              color: 'form.disabled',
              backgroundColor: 'form.disabled',
              borderColor: 'form.disabled',
              pointerEvents: 'none',
              topShadow: '0 0 0 0 transparent',
            },
            // turn off "wash" effect over disabled buttons
            '[aria-disabled="true"]::after': {
              display: 'none',
            },
          }}
          size={size}
          type="secondary"
          inherits={restProps.subviews.button}
          uses={[
            assignProps((props) => {
              const contextProps = useFieldContexts(props);
              return contextProps;
            }),
            assignProps(triggerProps as ButtonProps),
            assignProps((props) => ({
              // truncation styles can't be applied to flex containers.
              // and wrapping the entire content would break JSX-based item layouts
              children: React.Children.map(props.children, (child) =>
                typeof child === 'string' ? (
                  <TruncatedText> {child}</TruncatedText>
                ) : (
                  child
                ),
              ),
            })),
          ]}
        >
          {!selectedItems || selectedItems.length === 0 ? placeholder : null}
          {props.selectionMode === 'single'
            ? selectedItems?.[0]?.props.icon
            : null}
          {singleSelectionContent}
          {multipleSelectionContent}
          <Icon
            inherits={restProps.subviews.buttonIcon}
            icon={arrowUpDown}
            aria-hidden
            size="xsmall"
            css={{marginLeft: 'auto'}}
          />
        </Button>
        {!state.isOpen &&
        props.selectionMode === 'multiple' &&
        triggerTooltipEnabled &&
        selectedItems &&
        selectedItems.length > 0 ? (
          <Tooltip
            trigger={buttonRef}
            placement="top"
            delay={{show: 0, hide: 0}}
          >
            <view.div
              css={{
                display: '-webkit-box',
                lineClamp: 10,
                WebkitLineClamp: 10,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {showGroupLabels
                ? groupedSelectionPreviews
                    .map(
                      (preview) => `${preview.textValue}${preview.separator}`,
                    )
                    .join('')
                : selectedItems
                    .map(getSelectedItemTooltipText)
                    .filter(Boolean)
                    .join(', ')}
            </view.div>
          </Tooltip>
        ) : null}
        {/*
          This is a tradeoff we made when allowing items as renderable
          children. Unfortunately, we *always* need to render the collection
          so the items are always registered. For anything but simple cases,
          we will encourage users to use the `data` pattern but it's still
          worthwhile to support this for simple cases.
         */}
        <ListContext.Provider value={{selectionMode: props.selectionMode}}>
          <AnimatePresenceProvider
            emptyRender={
              children ? (
                <view.div css={{display: 'none'}}>
                  {props.selectionMode === 'multiple' ? fauxTree : listbox}
                </view.div>
              ) : null
            }
          >
            <Popover
              inherits={restProps.subviews.popover}
              open={state.isOpen}
              targetRef={buttonRef}
              placement="bottom left"
              shift={false}
              size={{
                maxHeight: 'fill',
                matchWidth: true,
              }}
              onClose={() => {
                state.close();
                if (isInSailForm && 'onBlur' in props) {
                  props.onBlur?.();
                }
              }}
              // Always use the max height of the Bottom sheet when filterable.
              // This is due to 2 reasons:
              // 1. To avoid the Bottom sheet height jumping around when different items are filtered.
              // 2. When opening the keyboard on the bottom part of the screen, mobile browsers will shift the viewport,
              //    cropping the top part of the screen until the keyboard is closed.
              //    This ensures the search input stays on the top part of the screen and the list is not cropped by the keyboard.
              bottomSheetAlwaysUseMaxHeight={filterable}
              disableDefaultBottomSheetScrollWrapper={filterable}
              css={{stack: 'y'}}
              bottomSheetTitle={
                (label === LABEL_OPT_OUT_STRING ? undefined : label) ||
                placeholder ||
                props['aria-label']
              }
            >
              {props.selectionMode === 'multiple' ? fauxTree : listbox}
            </Popover>
          </AnimatePresenceProvider>
        </ListContext.Provider>
      </FormField>
    );
  },
);
